import {
  Badge,
  Box,
  HStack,
  Heading,
  Icon,
  SimpleGrid,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Text,
  VStack,
  Tabs,
  Tooltip,
} from '@chakra-ui/react'
import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  FiFileText,
  FiGitBranch,
  FiGrid,
  FiRefreshCw,
  FiSettings,
  FiUploadCloud,
} from 'react-icons/fi'
import { useNetworkEnvironment } from '@/hooks/useNetworkEnvironment'
import { useToast } from '@/hooks/useToast'
import { DropdownSelect } from '@/components/common/DropdownSelect'
import { MainnetOnlyNotice } from '@/components/common/MainnetOnlyNotice'
import { RefreshIconButton } from '@/components/common/RefreshIconButton'
import {
  DocumentationContentConfirmDialog,
  type DocumentationContentConfirmDialogState,
} from './DocumentationContentConfirmDialog'
import { DocumentationContentPagesTab } from './DocumentationContentPagesTab'
import { DocumentationContentPublishTab } from './DocumentationContentPublishTab'
import { DocumentationContentSettingsTab } from './DocumentationContentSettingsTab'
import { DocumentationContentSyncTab } from './DocumentationContentSyncTab'
import { DocumentationContentVersionsTab } from './DocumentationContentVersionsTab'
import { useAdminTabQuery } from '@/navigation/adminTabQuery'
import { systemService } from '@/services/admin/systemService'
import {
  documentationService,
  type ContentType,
  type DocumentationConnectivityResult,
  type DocumentationDryRunResult,
  type DocumentationSyncExecutionResult,
  type DocumentationVersionActionExecutionResult,
  type DocumentationVersion,
  type VersionStatus,
  type SyncLogStatus,
} from '@/services/admin/documentationService'

const CONTENT_TYPES: ContentType[] = [
  'documentation',
  'terms',
  'privacy',
  'compliance',
  'restricted-countries-and-regions',
]
const MIN_DOCS_CACHE_TTL_SECONDS = 60
const TAB_STORAGE_KEY = 'admin-documentation-content-tab-index'
const DOCUMENTATION_CONTENT_TABS = [
  'overview',
  'versions',
  'pages',
  'publish',
  'sync_logs',
  'settings',
] as const

const VERSION_STATUSES = [
  { value: '', labelKey: 'documentationContent.statuses.all' },
  { value: 'draft', labelKey: 'documentationContent.statuses.draft' },
  { value: 'published', labelKey: 'documentationContent.statuses.published' },
  { value: 'archived', labelKey: 'documentationContent.statuses.archived' },
] as const

const SYNC_STATUSES = [
  { value: '', labelKey: 'documentationContent.statuses.all' },
  { value: 'success', labelKey: 'documentationContent.syncStatuses.success' },
  { value: 'ignored', labelKey: 'documentationContent.syncStatuses.ignored' },
  { value: 'failed', labelKey: 'documentationContent.syncStatuses.failed' },
] as const

const buildReplayTemplate = () =>
  JSON.stringify(
    {
      ref: 'refs/heads/main',
      after: 'replace-with-commit-sha',
      repository: {
        full_name: 'owner/repository',
      },
      commits: [
        {
          modified: ['docs/documentation/v1.0.0/getting-started.md'],
          added: [],
          removed: [],
        },
      ],
    },
    null,
    2
  )

const formatDateTime = (
  value?: string | null,
  fallback = 'N/A',
  locale = 'en-US'
) => {
  if (!value) return fallback
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) {
    return fallback
  }
  return parsed.toLocaleString(locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
}

const statusColor = (status: string) => {
  switch (status) {
    case 'published':
    case 'success':
      return 'green'
    case 'draft':
    case 'ignored':
      return 'yellow'
    case 'archived':
    case 'failed':
      return 'red'
    default:
      return 'gray'
  }
}

const resolveSyncMutationError = (
  error: any,
  fallback: string,
  timeoutHint: string
) => {
  const backendMessage =
    error?.response?.data?.error?.message ??
    error?.response?.data?.message ??
    error?.message ??
    fallback
  const errorCode = String(error?.code ?? '').toUpperCase()
  if (
    errorCode === 'ECONNABORTED' ||
    /timeout/iu.test(String(backendMessage))
  ) {
    return `${backendMessage}. ${timeoutHint}`
  }
  return backendMessage
}

const findLatestPublished = (versions: DocumentationVersion[]) => {
  const published = versions.filter(row => row.status === 'published')
  if (published.length === 0) {
    return null
  }
  return [...published].sort((a, b) => {
    const left = Date.parse(a.publishedAt ?? a.updatedAt)
    const right = Date.parse(b.publishedAt ?? b.updatedAt)
    return right - left
  })[0]
}

export function DocumentationContentPage() {
  const { t, i18n } = useTranslation()
  const toast = useToast()
  const queryClient = useQueryClient()
  const { currentEnv } = useNetworkEnvironment()
  const isMainnet = currentEnv === 'mainnet'

  const { activeTabIndex: tabIndex, handleTabChange } = useAdminTabQuery({
    tabs: DOCUMENTATION_CONTENT_TABS,
    defaultTab: 'overview',
    storageKey: TAB_STORAGE_KEY,
  })
  const isOverviewTab = tabIndex === 0
  const isVersionsTab = tabIndex === 1
  const isPagesTab = tabIndex === 2
  const isPublishTab = tabIndex === 3
  const isSyncLogsTab = tabIndex === 4
  const isSettingsTab = tabIndex === 5
  const formatDateTimeForLocale = (value?: string | null) =>
    formatDateTime(value, t('common.na'), i18n.language)
  const contentTypeLabel = (type: ContentType | string) =>
    t(`documentationContent.contentTypes.${type}`, { defaultValue: type })
  const statusLabel = (status: string) =>
    t(`documentationContent.statuses.${status}`, { defaultValue: status })
  const summarizeVersion = (version: DocumentationVersion) =>
    `${version.versionCode} (${statusLabel(version.status)})`

  const [contentType, setContentType] = useState<ContentType>('documentation')
  const [versionStatus, setVersionStatus] = useState<string>('')
  const [createVersionCode, setCreateVersionCode] = useState('')
  const [createVersionLabel, setCreateVersionLabel] = useState('')
  const [createSourceRef, setCreateSourceRef] = useState('')

  const [selectedVersionCode, setSelectedVersionCode] = useState('')
  const [selectedPageSlug, setSelectedPageSlug] = useState('')

  const [publishVersionCode, setPublishVersionCode] = useState('')
  const [publishSetDefault, setPublishSetDefault] = useState(true)
  const [defaultVersionCode, setDefaultVersionCode] = useState('')

  const [syncPage, setSyncPage] = useState(1)
  const [syncLimit] = useState(20)
  const [syncStatus, setSyncStatus] = useState<string>('')
  const [syncSourceRef, setSyncSourceRef] = useState('')
  const [syncDeliveryId, setSyncDeliveryId] = useState('')
  const [forcePublishedVersionCode, setForcePublishedVersionCode] = useState('')

  const [replayPayload, setReplayPayload] = useState(buildReplayTemplate())
  const [replaySourceRef, setReplaySourceRef] = useState('')
  const [replayDeliveryId, setReplayDeliveryId] = useState('')
  const [isReplayExpanded, setIsReplayExpanded] = useState(false)

  const [connectivityResult, setConnectivityResult] =
    useState<DocumentationConnectivityResult | null>(null)
  const [dryRunResult, setDryRunResult] =
    useState<DocumentationDryRunResult | null>(null)
  const [settingsRepository, setSettingsRepository] = useState('')
  const [settingsBranch, setSettingsBranch] = useState('')
  const [settingsPathGlob, setSettingsPathGlob] = useState('')
  const [settingsCacheSoftTtl, setSettingsCacheSoftTtl] = useState('')
  const [settingsCacheTtl, setSettingsCacheTtl] = useState('')
  const [settingsWebhookDedupTtl, setSettingsWebhookDedupTtl] = useState('')
  const [confirmDialog, setConfirmDialog] =
    useState<DocumentationContentConfirmDialogState | null>(null)

  const versionsQuery = useQuery({
    enabled: isMainnet && isVersionsTab,
    queryKey: [
      'documentation-content',
      'versions',
      currentEnv,
      contentType,
      versionStatus,
    ],
    queryFn: () =>
      documentationService.listVersions({
        contentType,
        status: (versionStatus || undefined) as VersionStatus | undefined,
      }),
  })

  const allVersionsQuery = useQuery({
    enabled:
      isMainnet &&
      (isOverviewTab || isPagesTab || isPublishTab || isSyncLogsTab),
    queryKey: [
      'documentation-content',
      'all-versions',
      currentEnv,
      contentType,
    ],
    queryFn: () => documentationService.listVersions({ contentType }),
  })

  const pagesQuery = useQuery({
    queryKey: [
      'documentation-content',
      'pages',
      currentEnv,
      contentType,
      selectedVersionCode,
    ],
    enabled: isMainnet && isPagesTab && Boolean(selectedVersionCode),
    queryFn: () =>
      documentationService.listVersionPages({
        contentType,
        versionCode: selectedVersionCode,
      }),
  })

  const pageDetailQuery = useQuery({
    queryKey: [
      'documentation-content',
      'page',
      currentEnv,
      contentType,
      selectedVersionCode,
      selectedPageSlug,
    ],
    enabled:
      isMainnet &&
      isPagesTab &&
      Boolean(selectedVersionCode) &&
      Boolean(selectedPageSlug),
    queryFn: () =>
      documentationService.getVersionPage({
        contentType,
        versionCode: selectedVersionCode,
        slug: selectedPageSlug,
      }),
  })

  const syncLogsQuery = useQuery({
    enabled: isMainnet && isSyncLogsTab,
    queryKey: [
      'documentation-content',
      'sync-logs',
      currentEnv,
      syncPage,
      syncLimit,
      syncStatus,
      syncSourceRef,
      syncDeliveryId,
    ],
    queryFn: () =>
      documentationService.listSyncLogs({
        page: syncPage,
        limit: syncLimit,
        status: (syncStatus || undefined) as SyncLogStatus | undefined,
        sourceRef: syncSourceRef || undefined,
        deliveryId: syncDeliveryId || undefined,
      }),
  })

  const settingsQuery = useQuery({
    enabled: isMainnet && (isSettingsTab || isSyncLogsTab),
    queryKey: ['documentation-content', 'settings', currentEnv],
    queryFn: () => documentationService.getSettings(),
    refetchOnWindowFocus: false,
  })

  const syncStatusQuery = useQuery({
    enabled: isMainnet && (isOverviewTab || isSyncLogsTab),
    queryKey: ['documentation-content', 'sync-status', currentEnv],
    queryFn: () => documentationService.getSyncStatus(),
    refetchOnWindowFocus: false,
  })

  const invalidateVersions = async () => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: ['documentation-content', 'versions', currentEnv],
      }),
      queryClient.invalidateQueries({
        queryKey: ['documentation-content', 'all-versions', currentEnv],
      }),
      queryClient.invalidateQueries({
        queryKey: ['documentation-content', 'pages', currentEnv],
      }),
      queryClient.invalidateQueries({
        queryKey: ['documentation-content', 'page', currentEnv],
      }),
    ])
  }

  const invalidateSyncLogs = async () => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: ['documentation-content', 'sync-logs', currentEnv],
      }),
      queryClient.invalidateQueries({
        queryKey: ['documentation-content', 'sync-status', currentEnv],
      }),
    ])
  }

  const createMutation = useMutation({
    mutationFn: () =>
      documentationService.createVersion({
        contentType,
        versionCode: createVersionCode.trim(),
        versionLabel: createVersionLabel.trim() || undefined,
        sourceRef: createSourceRef.trim() || undefined,
      }),
    onSuccess: async version => {
      await invalidateVersions()
      setCreateVersionCode('')
      setCreateVersionLabel('')
      setCreateSourceRef('')
      toast.success({
        title: t('documentationContent.toast.draftVersionCreated'),
        description: summarizeVersion(version),
      })
    },
    onError: (error: any) => {
      toast.error({
        title: t('documentationContent.toast.createVersionFailed'),
        description:
          error?.response?.data?.error?.message ??
          error?.message ??
          t('common.unknownError'),
      })
    },
  })

  const publishMutation = useMutation({
    mutationFn: ({
      versionCode,
      setDefault,
    }: {
      versionCode: string
      setDefault?: boolean
    }) =>
      documentationService.publishVersion(versionCode, {
        contentType,
        setDefault,
      }),
    onSuccess: async result => {
      await invalidateVersions()
      if (result.queued) {
        await invalidateSyncLogs()
        toast.success({
          title: t('documentationContent.toast.publishQueued'),
          description: t('documentationContent.toast.taskContentVersion', {
            taskId: result.taskId ?? t('common.na'),
            contentType: contentTypeLabel(result.contentType),
            versionCode: result.versionCode,
          }),
        })
        return
      }
      if (result.version) {
        toast.success({
          title: t('documentationContent.toast.versionPublished'),
          description: summarizeVersion(result.version),
        })
        return
      }
      toast.success({
        title: t('documentationContent.toast.publishRequestCompleted'),
        description: `${contentTypeLabel(result.contentType)}/${result.versionCode}`,
      })
    },
    onError: (error: any) => {
      toast.error({
        title: t('documentationContent.toast.publishVersionFailed'),
        description:
          error?.response?.data?.error?.message ??
          error?.message ??
          t('common.unknownError'),
      })
    },
  })

  const setDefaultMutation = useMutation({
    mutationFn: (versionCode: string) =>
      documentationService.setDefaultVersion(versionCode, contentType),
    onSuccess: async result => {
      await invalidateVersions()
      const queued = result as DocumentationVersionActionExecutionResult
      if (queued.queued) {
        await invalidateSyncLogs()
        toast.success({
          title: t('documentationContent.toast.setDefaultQueued'),
          description: t('documentationContent.toast.taskContentVersion', {
            taskId: queued.taskId ?? t('common.na'),
            contentType: contentTypeLabel(queued.contentType),
            versionCode: queued.versionCode,
          }),
        })
        return
      }
      const version = result as DocumentationVersion
      toast.success({
        title: t('documentationContent.toast.defaultVersionUpdated'),
        description: summarizeVersion(version),
      })
    },
    onError: (error: any) => {
      toast.error({
        title: t('documentationContent.toast.setDefaultVersionFailed'),
        description:
          error?.response?.data?.error?.message ??
          error?.message ??
          t('common.unknownError'),
      })
    },
  })

  const archiveMutation = useMutation({
    mutationFn: ({
      versionCode,
      replacementVersionCode,
    }: {
      versionCode: string
      replacementVersionCode?: string | null
    }) =>
      documentationService.archiveVersion(versionCode, {
        contentType,
        replacementVersionCode: replacementVersionCode ?? null,
      }),
    onSuccess: async result => {
      await invalidateVersions()
      const queued = result as DocumentationVersionActionExecutionResult
      if (queued.queued) {
        await invalidateSyncLogs()
        toast.success({
          title: t('documentationContent.toast.archiveQueued'),
          description: t('documentationContent.toast.taskContentVersion', {
            taskId: queued.taskId ?? t('common.na'),
            contentType: contentTypeLabel(queued.contentType),
            versionCode: queued.versionCode,
          }),
        })
        return
      }
      const version = result as DocumentationVersion
      toast.success({
        title: t('documentationContent.toast.versionArchived'),
        description: summarizeVersion(version),
      })
    },
    onError: (error: any) => {
      toast.error({
        title: t('documentationContent.toast.archiveVersionFailed'),
        description:
          error?.response?.data?.error?.message ??
          error?.message ??
          t('common.unknownError'),
      })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: ({
      versionCode,
      confirmVersionCode,
      replacementVersionCode,
      reason,
    }: {
      versionCode: string
      confirmVersionCode: string
      replacementVersionCode?: string | null
      reason?: string | null
    }) =>
      documentationService.deleteVersion(versionCode, {
        contentType,
        confirmVersionCode,
        replacementVersionCode: replacementVersionCode ?? null,
        reason: reason ?? null,
      }),
    onSuccess: async result => {
      await invalidateVersions()
      const queued = result as DocumentationVersionActionExecutionResult
      if (queued.queued) {
        await invalidateSyncLogs()
        toast.success({
          title: t('documentationContent.toast.deleteQueued'),
          description: t('documentationContent.toast.taskContentVersion', {
            taskId: queued.taskId ?? t('common.na'),
            contentType: contentTypeLabel(queued.contentType),
            versionCode: queued.versionCode,
          }),
        })
        return
      }
      const deleted = result as {
        versionCode: string
        deletedPages: number
      }
      toast.success({
        title: t('documentationContent.toast.versionDeleted'),
        description: t('documentationContent.toast.deletedVersionPages', {
          versionCode: deleted.versionCode,
          pages: deleted.deletedPages,
        }),
      })
    },
    onError: (error: any) => {
      toast.error({
        title: t('documentationContent.toast.deleteVersionFailed'),
        description:
          error?.response?.data?.error?.message ??
          error?.message ??
          t('common.unknownError'),
      })
    },
  })

  const replayMutation = useMutation({
    mutationFn: async () => {
      const parsed = JSON.parse(replayPayload)
      return documentationService.replaySync({
        payload: parsed as Record<string, unknown>,
        sourceRef: replaySourceRef.trim() || undefined,
        deliveryId: replayDeliveryId.trim() || undefined,
      })
    },
    onSuccess: async () => {
      await invalidateSyncLogs()
      toast.success({
        title: t('documentationContent.toast.syncReplayQueued'),
        description: t(
          'documentationContent.toast.syncReplayQueuedDescription'
        ),
      })
    },
    onError: (error: any) => {
      const fallback = t('documentationContent.toast.replayFailedFallback')
      toast.error({
        title: t('documentationContent.toast.replayFailed'),
        description:
          error?.response?.data?.error?.message ?? error?.message ?? fallback,
      })
    },
  })

  const quickSyncMutation = useMutation({
    mutationFn: () =>
      documentationService.quickSync({
        contentType,
      }),
    onSuccess: async (result: DocumentationSyncExecutionResult) => {
      await Promise.all([
        invalidateVersions(),
        invalidateSyncLogs(),
        syncStatusQuery.refetch(),
      ])
      if (result.queued) {
        toast.success({
          title: t('documentationContent.toast.quickSyncQueued'),
          description: t('documentationContent.toast.taskDelivery', {
            taskId: result.taskId ?? t('common.na'),
            deliveryId: result.deliveryId ?? t('common.na'),
          }),
        })
        return
      }
      if (result.ignored) {
        toast.warning({
          title: t('documentationContent.toast.quickSyncIgnored'),
          description:
            result.reason ?? t('documentationContent.toast.noMatchingChanges'),
        })
        return
      }
      toast.success({
        title: t('documentationContent.toast.quickSyncCompleted'),
        description: t('documentationContent.toast.upsertRemoved', {
          upserted: result.summary.upsertedPages,
          removed: result.summary.removedPages,
        }),
      })
    },
    onError: (error: any) => {
      toast.error({
        title: t('documentationContent.toast.quickSyncFailed'),
        description: resolveSyncMutationError(
          error,
          t('common.unknownError'),
          t('documentationContent.toast.timeoutHint')
        ),
      })
    },
  })

  const rebuildMutation = useMutation({
    mutationFn: () =>
      documentationService.rebuildSync({
        contentType,
      }),
    onSuccess: async (result: DocumentationSyncExecutionResult) => {
      await Promise.all([
        invalidateVersions(),
        invalidateSyncLogs(),
        syncStatusQuery.refetch(),
      ])
      if (result.queued) {
        toast.success({
          title: t('documentationContent.toast.rebuildQueued'),
          description: t('documentationContent.toast.taskDelivery', {
            taskId: result.taskId ?? t('common.na'),
            deliveryId: result.deliveryId ?? t('common.na'),
          }),
        })
        return
      }
      if (result.ignored) {
        toast.warning({
          title: t('documentationContent.toast.rebuildIgnored'),
          description:
            result.reason ?? t('documentationContent.toast.noMatchingFiles'),
        })
        return
      }
      toast.success({
        title: t('documentationContent.toast.rebuildCompleted'),
        description: t('documentationContent.toast.upsertVersions', {
          upserted: result.summary.upsertedPages,
          versions: result.summary.touchedVersions,
        }),
      })
    },
    onError: (error: any) => {
      toast.error({
        title: t('documentationContent.toast.rebuildFailed'),
        description: resolveSyncMutationError(
          error,
          t('common.unknownError'),
          t('documentationContent.toast.timeoutHint')
        ),
      })
    },
  })

  const forcePublishedSyncMutation = useMutation({
    mutationFn: (versionCode: string) =>
      documentationService.forcePublishedSync({
        contentType,
        versionCode,
        confirmVersionCode: versionCode,
      }),
    onSuccess: async (result: DocumentationSyncExecutionResult) => {
      await Promise.all([
        invalidateVersions(),
        invalidateSyncLogs(),
        syncStatusQuery.refetch(),
      ])
      if (result.queued) {
        toast.success({
          title: t('documentationContent.toast.forceSyncQueued'),
          description: t('documentationContent.toast.taskVersion', {
            taskId: result.taskId ?? t('common.na'),
            versionCode: result.versionCode ?? t('common.na'),
          }),
        })
        return
      }
      if (result.ignored) {
        toast.warning({
          title: t('documentationContent.toast.forceSyncIgnored'),
          description:
            result.reason ?? t('documentationContent.toast.noMatchingFiles'),
        })
        return
      }
      toast.success({
        title: t('documentationContent.toast.forceSyncCompleted'),
        description: t('documentationContent.toast.upsertRemoved', {
          upserted: result.summary.upsertedPages,
          removed: result.summary.removedPages,
        }),
      })
    },
    onError: (error: any) => {
      toast.error({
        title: t('documentationContent.toast.forceSyncFailed'),
        description: resolveSyncMutationError(
          error,
          t('common.unknownError'),
          t('documentationContent.toast.timeoutHint')
        ),
      })
    },
  })

  const connectivityMutation = useMutation({
    mutationFn: () => documentationService.testConnectivity(),
    onSuccess: result => {
      setConnectivityResult(result)
      if (result.ok) {
        toast.success({
          title: t('documentationContent.toast.connectivityTestPassed'),
          description: result.message,
        })
      } else {
        toast.warning({
          title: t('documentationContent.toast.connectivityTestFailed'),
          description: result.message,
        })
      }
    },
    onError: (error: any) => {
      toast.error({
        title: t('documentationContent.toast.connectivityTestFailed'),
        description:
          error?.response?.data?.error?.message ??
          error?.message ??
          t('common.unknownError'),
      })
    },
  })

  const dryRunMutation = useMutation({
    mutationFn: async () => {
      const parsed = JSON.parse(replayPayload)
      return documentationService.dryRunSync({
        payload: parsed as Record<string, unknown>,
        sourceRef: replaySourceRef.trim() || undefined,
        deliveryId: replayDeliveryId.trim() || undefined,
      })
    },
    onSuccess: result => {
      setDryRunResult(result)
      if (result.ignored) {
        toast.warning({
          title: t('documentationContent.toast.dryRunIgnored'),
          description:
            result.reason ?? t('documentationContent.toast.ignoredByFilters'),
        })
      } else {
        toast.success({
          title: t('documentationContent.toast.dryRunCompleted'),
          description: t('documentationContent.toast.previewReady'),
        })
      }
    },
    onError: (error: any) => {
      toast.error({
        title: t('documentationContent.toast.dryRunFailed'),
        description:
          error?.response?.data?.error?.message ??
          error?.message ??
          t('common.unknownError'),
      })
    },
  })

  const saveSettingsMutation = useMutation({
    mutationFn: async () => {
      const repository = settingsRepository.trim()
      const branch = settingsBranch.trim()
      const pathGlob = settingsPathGlob.trim()
      const cacheSoftTtl = Number.parseInt(settingsCacheSoftTtl.trim(), 10)
      const cacheTtl = Number.parseInt(settingsCacheTtl.trim(), 10)
      const webhookDedupTtl = Number.parseInt(
        settingsWebhookDedupTtl.trim(),
        10
      )

      if (!repository) {
        throw new Error(t('documentationContent.validation.repositoryRequired'))
      }
      if (!branch) {
        throw new Error(t('documentationContent.validation.branchRequired'))
      }
      if (!pathGlob) {
        throw new Error(t('documentationContent.validation.pathGlobRequired'))
      }
      if (!Number.isFinite(cacheTtl) || cacheTtl < MIN_DOCS_CACHE_TTL_SECONDS) {
        throw new Error(
          t('documentationContent.validation.docsCacheTtlMin', {
            seconds: MIN_DOCS_CACHE_TTL_SECONDS,
          })
        )
      }
      if (
        !Number.isFinite(cacheSoftTtl) ||
        cacheSoftTtl < MIN_DOCS_CACHE_TTL_SECONDS
      ) {
        throw new Error(
          t('documentationContent.validation.docsCacheSoftTtlMin', {
            seconds: MIN_DOCS_CACHE_TTL_SECONDS,
          })
        )
      }
      if (cacheSoftTtl >= cacheTtl) {
        throw new Error(
          t('documentationContent.validation.docsCacheSoftTtlLessThanHard')
        )
      }
      if (
        !Number.isFinite(webhookDedupTtl) ||
        webhookDedupTtl < MIN_DOCS_CACHE_TTL_SECONDS
      ) {
        throw new Error(
          t('documentationContent.validation.webhookDedupTtlMin', {
            seconds: MIN_DOCS_CACHE_TTL_SECONDS,
          })
        )
      }

      const current = await systemService.getRuntimeConfigCurrent('mainnet')
      if (!current) {
        throw new Error(
          t('documentationContent.validation.runtimeConfigMissing')
        )
      }

      const currentConfig = current.configJson
      if (
        !currentConfig ||
        typeof currentConfig !== 'object' ||
        Array.isArray(currentConfig)
      ) {
        throw new Error(
          t('documentationContent.validation.runtimeConfigInvalid')
        )
      }

      const nextConfig = {
        ...(currentConfig as Record<string, unknown>),
        DOCS_SYNC_REPOSITORY: repository,
        DOCS_SYNC_BRANCH: branch,
        DOCS_SYNC_PATH_GLOB: pathGlob,
        DOCS_CACHE_SOFT_TTL_SECONDS: cacheSoftTtl,
        DOCS_CACHE_TTL_SECONDS: cacheTtl,
        DOCS_WEBHOOK_DEDUP_TTL_SECONDS: webhookDedupTtl,
      }

      return await systemService.updateRuntimeConfigCurrent(
        {
          configJson: nextConfig,
          expectedUpdatedAt: current.updatedAt,
        },
        'mainnet'
      )
    },
    onSuccess: async result => {
      await settingsQuery.refetch()
      if (result.cacheDegraded || result.auditDegraded) {
        toast.warning({
          title: t('documentationContent.toast.settingsSavedDegraded', {
            defaultValue: 'Settings saved; follow-up action degraded',
          }),
          description: [
            result.cacheDegraded
              ? t('systemConfig.runtimeConfig.cacheRefreshDegraded', {
                  defaultValue: 'cache refresh',
                })
              : null,
            result.auditDegraded
              ? t('systemConfig.runtimeConfig.auditWriteDegraded', {
                  defaultValue: 'audit logging',
                })
              : null,
          ]
            .filter(Boolean)
            .join(', '),
        })
        return
      }
      toast.success({
        title: t('documentationContent.toast.settingsSaved'),
        description: t('documentationContent.toast.settingsSavedDescription'),
      })
    },
    onError: (error: any) => {
      toast.error({
        title: t('documentationContent.toast.saveSettingsFailed'),
        description:
          error?.response?.data?.error?.message ??
          error?.message ??
          t('common.unknownError'),
      })
    },
  })

  const versionRows = versionsQuery.data?.versions ?? []
  const allVersionRows = allVersionsQuery.data?.versions ?? []
  const syncRows = syncLogsQuery.data?.logs ?? []
  const syncPagination = syncLogsQuery.data?.pagination

  const publishedVersionCodes = useMemo(
    () =>
      allVersionRows
        .filter(row => row.status === 'published')
        .map(row => row.versionCode),
    [allVersionRows]
  )

  const draftVersionCodes = useMemo(
    () =>
      allVersionRows
        .filter(row => row.status === 'draft')
        .map(row => row.versionCode),
    [allVersionRows]
  )

  const nonDefaultPublishedVersionCodes = useMemo(
    () =>
      allVersionRows
        .filter(row => row.status === 'published' && !row.isDefault)
        .map(row => row.versionCode),
    [allVersionRows]
  )

  const overviewDefaultVersion = allVersionRows.find(row => row.isDefault)
  const overviewLatestPublished = findLatestPublished(allVersionRows)
  const overviewLastSync = syncStatusQuery.data?.latest ?? syncRows[0]

  useEffect(() => {
    const preferred =
      allVersionRows.find(row => row.isDefault)?.versionCode ??
      allVersionRows[0]?.versionCode ??
      ''
    if (!preferred) {
      setSelectedVersionCode('')
      return
    }
    if (
      !selectedVersionCode ||
      !allVersionRows.some(row => row.versionCode === selectedVersionCode)
    ) {
      setSelectedVersionCode(preferred)
    }
  }, [allVersionRows, selectedVersionCode])

  useEffect(() => {
    if (draftVersionCodes.length === 0) {
      setPublishVersionCode('')
      return
    }
    if (
      !publishVersionCode ||
      !draftVersionCodes.includes(publishVersionCode)
    ) {
      setPublishVersionCode(draftVersionCodes[0])
    }
  }, [draftVersionCodes, publishVersionCode])

  useEffect(() => {
    if (nonDefaultPublishedVersionCodes.length === 0) {
      setDefaultVersionCode('')
      return
    }
    if (
      !defaultVersionCode ||
      !nonDefaultPublishedVersionCodes.includes(defaultVersionCode)
    ) {
      setDefaultVersionCode(nonDefaultPublishedVersionCodes[0])
    }
  }, [nonDefaultPublishedVersionCodes, defaultVersionCode])

  useEffect(() => {
    if (publishedVersionCodes.length === 0) {
      setForcePublishedVersionCode('')
      return
    }
    if (
      !forcePublishedVersionCode ||
      !publishedVersionCodes.includes(forcePublishedVersionCode)
    ) {
      setForcePublishedVersionCode(publishedVersionCodes[0])
    }
  }, [publishedVersionCodes, forcePublishedVersionCode])

  useEffect(() => {
    const rows = pagesQuery.data?.pages ?? []
    if (rows.length === 0) {
      setSelectedPageSlug('')
      return
    }
    if (!selectedPageSlug || !rows.some(row => row.slug === selectedPageSlug)) {
      setSelectedPageSlug(rows[0].slug)
    }
  }, [pagesQuery.data?.pages, selectedPageSlug])

  useEffect(() => {
    const settings = settingsQuery.data
    if (!settings) {
      return
    }
    setSettingsRepository(settings.repository)
    setSettingsBranch(settings.branch)
    setSettingsPathGlob(settings.pathGlob)
    setSettingsCacheSoftTtl(String(settings.cache.softTtlSeconds))
    setSettingsCacheTtl(String(settings.cache.ttlSeconds))
    setSettingsWebhookDedupTtl(String(settings.cache.webhookDedupTtlSeconds))
  }, [settingsQuery.data])

  const handleCreateDraft = () => {
    if (!createVersionCode.trim()) {
      toast.warning({
        title: t('documentationContent.validation.versionCodeRequired'),
      })
      return
    }
    createMutation.mutate()
  }

  const openPublishConfirm = (versionCode: string, setDefault: boolean) => {
    setConfirmDialog({
      type: 'publish',
      versionCode,
      setDefault,
      replacementVersionCode: '',
      confirmVersionCode: '',
      deleteReason: '',
    })
  }

  const openArchiveConfirm = (row: DocumentationVersion) => {
    const suggestedReplacement =
      publishedVersionCodes.find(code => code !== row.versionCode) ?? ''
    setConfirmDialog({
      type: 'archive',
      versionCode: row.versionCode,
      requiresReplacement: row.isDefault,
      replacementVersionCode: suggestedReplacement,
      confirmVersionCode: '',
      deleteReason: '',
    })
  }

  const openSetDefaultConfirm = (versionCode: string) => {
    setConfirmDialog({
      type: 'set-default',
      versionCode,
      replacementVersionCode: '',
      confirmVersionCode: '',
      deleteReason: '',
    })
  }

  const openDeleteConfirm = (row: DocumentationVersion) => {
    const suggestedReplacement =
      publishedVersionCodes.find(code => code !== row.versionCode) ?? ''
    setConfirmDialog({
      type: 'delete',
      versionCode: row.versionCode,
      requiresReplacement: row.isDefault,
      replacementVersionCode: suggestedReplacement,
      confirmVersionCode: '',
      deleteReason: '',
    })
  }

  const openQuickSyncConfirm = () => {
    setConfirmDialog({
      type: 'quick-sync',
      versionCode: '',
      replacementVersionCode: '',
      confirmVersionCode: '',
      deleteReason: '',
    })
  }

  const openRebuildConfirm = () => {
    setConfirmDialog({
      type: 'rebuild-sync',
      versionCode: '',
      replacementVersionCode: '',
      confirmVersionCode: '',
      deleteReason: '',
    })
  }

  const openForcePublishedSyncConfirm = () => {
    if (!forcePublishedVersionCode) {
      toast.warning({
        title: t('documentationContent.validation.selectPublishedVersion'),
      })
      return
    }
    setConfirmDialog({
      type: 'force-published-sync',
      versionCode: forcePublishedVersionCode,
      replacementVersionCode: '',
      confirmVersionCode: '',
      deleteReason: '',
    })
  }

  const closeConfirmDialog = () => {
    setConfirmDialog(null)
  }

  const handleConfirmAction = () => {
    if (!confirmDialog) {
      return
    }
    if (confirmDialog.type === 'quick-sync') {
      quickSyncMutation.mutate()
      closeConfirmDialog()
      return
    }
    if (confirmDialog.type === 'rebuild-sync') {
      rebuildMutation.mutate()
      closeConfirmDialog()
      return
    }
    if (confirmDialog.type === 'force-published-sync') {
      const confirmVersionCode = confirmDialog.confirmVersionCode.trim()
      if (confirmVersionCode !== confirmDialog.versionCode) {
        toast.warning({
          title: t('documentationContent.validation.confirmForceSyncVersion'),
        })
        return
      }
      forcePublishedSyncMutation.mutate(confirmDialog.versionCode)
      closeConfirmDialog()
      return
    }
    if (confirmDialog.type === 'publish') {
      publishMutation.mutate({
        versionCode: confirmDialog.versionCode,
        setDefault: Boolean(confirmDialog.setDefault),
      })
      closeConfirmDialog()
      return
    }
    if (confirmDialog.type === 'set-default') {
      setDefaultMutation.mutate(confirmDialog.versionCode)
      closeConfirmDialog()
      return
    }

    if (confirmDialog.type === 'delete') {
      const confirmVersionCode = confirmDialog.confirmVersionCode.trim()
      if (confirmVersionCode !== confirmDialog.versionCode) {
        toast.warning({
          title: t('documentationContent.validation.confirmDeleteVersion'),
        })
        return
      }
      const replacementVersionCode =
        confirmDialog.replacementVersionCode.trim() || null
      if (confirmDialog.requiresReplacement && !replacementVersionCode) {
        toast.warning({
          title: t('documentationContent.validation.replacementRequired'),
        })
        return
      }
      deleteMutation.mutate({
        versionCode: confirmDialog.versionCode,
        confirmVersionCode,
        replacementVersionCode,
        reason: confirmDialog.deleteReason.trim() || null,
      })
      closeConfirmDialog()
      return
    }

    const replacementVersionCode =
      confirmDialog.replacementVersionCode.trim() || null
    if (confirmDialog.requiresReplacement && !replacementVersionCode) {
      toast.warning({
        title: t('documentationContent.validation.replacementRequired'),
      })
      return
    }
    archiveMutation.mutate({
      versionCode: confirmDialog.versionCode,
      replacementVersionCode,
    })
    closeConfirmDialog()
  }

  const handlePublishFromTab = () => {
    if (!publishVersionCode) {
      toast.warning({
        title: t('documentationContent.validation.selectDraftVersion'),
      })
      return
    }
    openPublishConfirm(publishVersionCode, publishSetDefault)
  }

  const handleSetDefaultFromTab = () => {
    if (!defaultVersionCode) {
      toast.warning({
        title: t('documentationContent.validation.selectPublishedVersion'),
      })
      return
    }
    setDefaultMutation.mutate(defaultVersionCode)
  }

  const parsedSettingsCacheTtl = Number.parseInt(settingsCacheTtl.trim(), 10)
  const parsedSettingsCacheSoftTtl = Number.parseInt(
    settingsCacheSoftTtl.trim(),
    10
  )
  const parsedSettingsWebhookDedupTtl = Number.parseInt(
    settingsWebhookDedupTtl.trim(),
    10
  )
  const settingsCacheTtlInvalid =
    !Number.isFinite(parsedSettingsCacheTtl) ||
    parsedSettingsCacheTtl < MIN_DOCS_CACHE_TTL_SECONDS
  const settingsCacheSoftTtlInvalid =
    !Number.isFinite(parsedSettingsCacheSoftTtl) ||
    parsedSettingsCacheSoftTtl < MIN_DOCS_CACHE_TTL_SECONDS ||
    (Number.isFinite(parsedSettingsCacheTtl) &&
      parsedSettingsCacheTtl >= MIN_DOCS_CACHE_TTL_SECONDS &&
      parsedSettingsCacheSoftTtl >= parsedSettingsCacheTtl)
  const settingsWebhookDedupTtlInvalid =
    !Number.isFinite(parsedSettingsWebhookDedupTtl) ||
    parsedSettingsWebhookDedupTtl < MIN_DOCS_CACHE_TTL_SECONDS
  const settingsInputInvalid =
    settingsRepository.trim().length === 0 ||
    settingsBranch.trim().length === 0 ||
    settingsPathGlob.trim().length === 0 ||
    settingsCacheSoftTtlInvalid ||
    settingsCacheTtlInvalid ||
    settingsWebhookDedupTtlInvalid

  const settingsLoadError = settingsQuery.error
    ? settingsQuery.error instanceof Error
      ? settingsQuery.error.message
      : t('documentationContent.settings.runtimeSettingsLoadFailed')
    : null
  const settingsHasInput =
    settingsRepository.trim().length > 0 ||
    settingsBranch.trim().length > 0 ||
    settingsPathGlob.trim().length > 0 ||
    settingsCacheSoftTtl.trim().length > 0 ||
    settingsCacheTtl.trim().length > 0 ||
    settingsWebhookDedupTtl.trim().length > 0
  const syncConfigReady = Boolean(settingsQuery.data) && !settingsLoadError

  const settingsDirty = (() => {
    const current = settingsQuery.data
    if (!current) {
      return Boolean(settingsLoadError) && settingsHasInput
    }
    return (
      settingsRepository.trim() !== current.repository ||
      settingsBranch.trim() !== current.branch ||
      settingsPathGlob.trim() !== current.pathGlob ||
      parsedSettingsCacheSoftTtl !== current.cache.softTtlSeconds ||
      parsedSettingsCacheTtl !== current.cache.ttlSeconds ||
      parsedSettingsWebhookDedupTtl !== current.cache.webhookDedupTtlSeconds
    )
  })()

  const resetSettingsForm = () => {
    const current = settingsQuery.data
    if (!current) {
      return
    }
    setSettingsRepository(current.repository)
    setSettingsBranch(current.branch)
    setSettingsPathGlob(current.pathGlob)
    setSettingsCacheSoftTtl(String(current.cache.softTtlSeconds))
    setSettingsCacheTtl(String(current.cache.ttlSeconds))
    setSettingsWebhookDedupTtl(String(current.cache.webhookDedupTtlSeconds))
  }

  const anyVersionPending =
    createMutation.isPending ||
    publishMutation.isPending ||
    setDefaultMutation.isPending ||
    archiveMutation.isPending ||
    deleteMutation.isPending

  if (!isMainnet) {
    return <MainnetOnlyNotice />
  }

  return (
    <Box>
      <VStack align="stretch" spacing={4}>
        <Box>
          <Heading size="lg" mb={1}>
            {t('documentationContent.title')}
          </Heading>
          <Text color="gray.600" fontSize="sm">
            {t('documentationContent.subtitle')}
          </Text>
        </Box>

        <Tabs
          variant="enclosed"
          size="sm"
          isLazy
          lazyBehavior="keepMounted"
          index={tabIndex}
          onChange={handleTabChange}
        >
          <TabList
            overflowX={{ base: 'auto', md: 'visible' }}
            overflowY="hidden"
          >
            <Tab
              aria-label={t('documentationContent.tabs.overview')}
              minW="56px"
              justifyContent="center"
            >
              <Tooltip label={t('documentationContent.tabs.overview')} hasArrow>
                <Box display="flex" alignItems="center">
                  <Icon as={FiGrid} boxSize={6} />
                </Box>
              </Tooltip>
            </Tab>
            <Tab
              aria-label={t('documentationContent.tabs.versions')}
              minW="56px"
              justifyContent="center"
            >
              <Tooltip label={t('documentationContent.tabs.versions')} hasArrow>
                <Box display="flex" alignItems="center">
                  <Icon as={FiGitBranch} boxSize={6} />
                </Box>
              </Tooltip>
            </Tab>
            <Tab
              aria-label={t('documentationContent.tabs.pages')}
              minW="56px"
              justifyContent="center"
            >
              <Tooltip label={t('documentationContent.tabs.pages')} hasArrow>
                <Box display="flex" alignItems="center">
                  <Icon as={FiFileText} boxSize={6} />
                </Box>
              </Tooltip>
            </Tab>
            <Tab
              aria-label={t('documentationContent.tabs.publish')}
              minW="56px"
              justifyContent="center"
            >
              <Tooltip label={t('documentationContent.tabs.publish')} hasArrow>
                <Box display="flex" alignItems="center">
                  <Icon as={FiUploadCloud} boxSize={6} />
                </Box>
              </Tooltip>
            </Tab>
            <Tab
              aria-label={t('documentationContent.tabs.syncLogs')}
              minW="56px"
              justifyContent="center"
            >
              <Tooltip label={t('documentationContent.tabs.syncLogs')} hasArrow>
                <Box display="flex" alignItems="center">
                  <Icon as={FiRefreshCw} boxSize={6} />
                </Box>
              </Tooltip>
            </Tab>
            <Tab
              aria-label={t('common.settings')}
              minW="56px"
              justifyContent="center"
            >
              <Tooltip label={t('common.settings')} hasArrow>
                <Box display="flex" alignItems="center">
                  <Icon as={FiSettings} boxSize={6} />
                </Box>
              </Tooltip>
            </Tab>
          </TabList>
          <HStack spacing={3} flexWrap="wrap" pt={3} pb={3}>
            <Text fontSize="sm" color="gray.600">
              {t('documentationContent.contentType')}
            </Text>
            <Box w="220px">
              <DropdownSelect
                size="sm"
                value={contentType}
                options={CONTENT_TYPES.map(type => ({
                  value: type,
                  label: contentTypeLabel(type),
                }))}
                onChange={value => {
                  setContentType(value as ContentType)
                  setVersionStatus('')
                  setSyncPage(1)
                }}
              />
            </Box>
            {tabIndex <= 1 ? (
              <RefreshIconButton
                label={t('documentationContent.versions.refreshVersions')}
                onClick={() => {
                  allVersionsQuery.refetch()
                  versionsQuery.refetch()
                }}
                isLoading={
                  allVersionsQuery.isFetching || versionsQuery.isFetching
                }
              />
            ) : null}
            {tabIndex === 2 ? (
              <>
                <Box flex="1" minW={{ base: '0', md: '80px' }} />
                <Box w="260px">
                  <DropdownSelect
                    size="sm"
                    value={selectedVersionCode}
                    options={allVersionRows.map(version => ({
                      value: version.versionCode,
                      label: `${version.versionCode} (${t(
                        `documentationContent.statuses.${version.status}`,
                        {
                          defaultValue: version.status,
                        }
                      )})`,
                    }))}
                    placeholder={t('documentationContent.pages.selectVersion')}
                    onChange={value => setSelectedVersionCode(value)}
                  />
                </Box>
                <RefreshIconButton
                  label={t('documentationContent.pages.refreshPages')}
                  onClick={() => pagesQuery.refetch()}
                  isLoading={pagesQuery.isFetching}
                  isDisabled={!selectedVersionCode}
                />
              </>
            ) : null}
          </HStack>

          <TabPanels>
            <TabPanel px={0} py={4}>
              <VStack align="stretch" spacing={4}>
                <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} spacing={4}>
                  <Box borderWidth="1px" borderRadius="md" p={3}>
                    <Text color="gray.500" fontSize="sm">
                      {t('documentationContent.overview.defaultVersion')}
                    </Text>
                    <Text fontWeight="semibold" mt={1}>
                      {overviewDefaultVersion?.versionCode ?? t('common.na')}
                    </Text>
                    <Text fontSize="xs" color="gray.500" mt={1}>
                      {overviewDefaultVersion?.versionLabel ??
                        t('documentationContent.overview.noDefaultSet')}
                    </Text>
                  </Box>
                  <Box borderWidth="1px" borderRadius="md" p={3}>
                    <Text color="gray.500" fontSize="sm">
                      {t('documentationContent.overview.latestPublished')}
                    </Text>
                    <Text fontWeight="semibold" mt={1}>
                      {overviewLatestPublished?.versionCode ?? t('common.na')}
                    </Text>
                    <Text fontSize="xs" color="gray.500" mt={1}>
                      {formatDateTimeForLocale(
                        overviewLatestPublished?.publishedAt
                      )}
                    </Text>
                  </Box>
                  <Box borderWidth="1px" borderRadius="md" p={3}>
                    <Text color="gray.500" fontSize="sm">
                      {t('documentationContent.overview.draftVersions')}
                    </Text>
                    <Text fontWeight="semibold" mt={1}>
                      {draftVersionCodes.length}
                    </Text>
                  </Box>
                  <Box borderWidth="1px" borderRadius="md" p={3}>
                    <Text color="gray.500" fontSize="sm">
                      {t('documentationContent.overview.lastSyncStatus')}
                    </Text>
                    <HStack mt={1} spacing={2}>
                      <Badge
                        colorScheme={statusColor(
                          overviewLastSync?.status ?? 'gray'
                        )}
                      >
                        {overviewLastSync?.status
                          ? t(
                              `documentationContent.syncStatuses.${overviewLastSync.status}`,
                              {
                                defaultValue: overviewLastSync.status,
                              }
                            )
                          : t('common.na')}
                      </Badge>
                      <Text fontSize="xs" color="gray.500">
                        {formatDateTimeForLocale(
                          overviewLastSync?.finishedAt ??
                            overviewLastSync?.createdAt
                        )}
                      </Text>
                    </HStack>
                  </Box>
                </SimpleGrid>
              </VStack>
            </TabPanel>

            <TabPanel px={0} py={4}>
              <DocumentationContentVersionsTab
                versionStatusOptions={VERSION_STATUSES.map(item => ({
                  value: item.value,
                  label: t(item.labelKey),
                }))}
                versionStatus={versionStatus}
                createVersionCode={createVersionCode}
                createVersionLabel={createVersionLabel}
                createSourceRef={createSourceRef}
                versionRows={versionRows}
                versionsLoading={versionsQuery.isLoading}
                anyVersionPending={anyVersionPending}
                publishPending={publishMutation.isPending}
                archivePending={archiveMutation.isPending}
                deletePending={deleteMutation.isPending}
                setDefaultPending={setDefaultMutation.isPending}
                createPending={createMutation.isPending}
                onSetVersionStatus={setVersionStatus}
                onSetCreateVersionCode={setCreateVersionCode}
                onSetCreateVersionLabel={setCreateVersionLabel}
                onSetCreateSourceRef={setCreateSourceRef}
                onCreateDraft={handleCreateDraft}
                onOpenPublishConfirm={openPublishConfirm}
                onOpenSetDefaultConfirm={openSetDefaultConfirm}
                onOpenArchiveConfirm={openArchiveConfirm}
                onOpenDeleteConfirm={openDeleteConfirm}
                statusColor={statusColor}
                formatDateTime={formatDateTimeForLocale}
              />
            </TabPanel>

            <TabPanel px={0} py={4}>
              <DocumentationContentPagesTab
                isLoading={pagesQuery.isLoading}
                sections={pagesQuery.data?.sections ?? []}
                selectedPageSlug={selectedPageSlug}
                pageDetailLoading={pageDetailQuery.isLoading}
                pageDetail={pageDetailQuery.data?.page}
                onSelectPageSlug={setSelectedPageSlug}
                formatDateTime={formatDateTimeForLocale}
              />
            </TabPanel>

            <TabPanel px={0} py={4}>
              <DocumentationContentPublishTab
                draftVersionCodes={draftVersionCodes}
                nonDefaultPublishedVersionCodes={
                  nonDefaultPublishedVersionCodes
                }
                publishVersionCode={publishVersionCode}
                publishSetDefault={publishSetDefault}
                defaultVersionCode={defaultVersionCode}
                publishPending={publishMutation.isPending}
                setDefaultPending={setDefaultMutation.isPending}
                anyVersionPending={anyVersionPending}
                onSetPublishVersionCode={setPublishVersionCode}
                onSetPublishSetDefault={setPublishSetDefault}
                onSetDefaultVersionCode={setDefaultVersionCode}
                onPublishFromTab={handlePublishFromTab}
                onSetDefaultFromTab={handleSetDefaultFromTab}
              />
            </TabPanel>

            <TabPanel px={0} py={4}>
              <DocumentationContentSyncTab
                syncStatusOptions={SYNC_STATUSES.map(item => ({
                  value: item.value,
                  label: t(item.labelKey),
                }))}
                syncStatusQuery={syncStatusQuery}
                syncLogsQuery={syncLogsQuery}
                syncRows={syncRows}
                syncPagination={syncPagination}
                syncPage={syncPage}
                syncStatus={syncStatus}
                syncSourceRef={syncSourceRef}
                syncDeliveryId={syncDeliveryId}
                replayPayload={replayPayload}
                replaySourceRef={replaySourceRef}
                replayDeliveryId={replayDeliveryId}
                isReplayExpanded={isReplayExpanded}
                dryRunResult={dryRunResult}
                syncConfigReady={syncConfigReady}
                syncConfigLoading={settingsQuery.isLoading}
                syncConfigError={settingsLoadError}
                publishedVersionOptions={publishedVersionCodes.map(code => ({
                  value: code,
                  label: code,
                }))}
                forcePublishedVersionCode={forcePublishedVersionCode}
                quickSyncPending={quickSyncMutation.isPending}
                rebuildPending={rebuildMutation.isPending}
                forcePublishedSyncPending={forcePublishedSyncMutation.isPending}
                replayPending={replayMutation.isPending}
                dryRunPending={dryRunMutation.isPending}
                onOpenQuickSyncConfirm={openQuickSyncConfirm}
                onOpenRebuildConfirm={openRebuildConfirm}
                onOpenForcePublishedSyncConfirm={openForcePublishedSyncConfirm}
                onSetForcePublishedVersionCode={setForcePublishedVersionCode}
                onSetReplayExpanded={setIsReplayExpanded}
                onSetReplayPayload={setReplayPayload}
                onSetReplaySourceRef={setReplaySourceRef}
                onSetReplayDeliveryId={setReplayDeliveryId}
                onDryRun={() => dryRunMutation.mutate()}
                onReplay={() => replayMutation.mutate()}
                onSetSyncStatus={setSyncStatus}
                onSetSyncSourceRef={setSyncSourceRef}
                onSetSyncDeliveryId={setSyncDeliveryId}
                onSetSyncPage={setSyncPage}
                formatDateTime={formatDateTimeForLocale}
                statusColor={statusColor}
              />
            </TabPanel>

            <TabPanel px={0} py={4}>
              <DocumentationContentSettingsTab
                isLoading={settingsQuery.isLoading}
                settingsLoadError={settingsLoadError}
                settings={settingsQuery.data}
                connectivityResult={connectivityResult}
                settingsRepository={settingsRepository}
                settingsBranch={settingsBranch}
                settingsPathGlob={settingsPathGlob}
                settingsCacheSoftTtl={settingsCacheSoftTtl}
                settingsCacheTtl={settingsCacheTtl}
                settingsWebhookDedupTtl={settingsWebhookDedupTtl}
                minDocsCacheTtlSeconds={MIN_DOCS_CACHE_TTL_SECONDS}
                settingsCacheSoftTtlInvalid={settingsCacheSoftTtlInvalid}
                settingsCacheTtlInvalid={settingsCacheTtlInvalid}
                settingsWebhookDedupTtlInvalid={settingsWebhookDedupTtlInvalid}
                settingsDirty={settingsDirty}
                settingsInputInvalid={settingsInputInvalid}
                savePending={saveSettingsMutation.isPending}
                connectivityPending={connectivityMutation.isPending}
                settingsFetching={settingsQuery.isFetching}
                onSetSettingsRepository={setSettingsRepository}
                onSetSettingsBranch={setSettingsBranch}
                onSetSettingsPathGlob={setSettingsPathGlob}
                onSetSettingsCacheSoftTtl={setSettingsCacheSoftTtl}
                onSetSettingsCacheTtl={setSettingsCacheTtl}
                onSetSettingsWebhookDedupTtl={setSettingsWebhookDedupTtl}
                onSave={() => saveSettingsMutation.mutate()}
                onReset={resetSettingsForm}
                onRunConnectivityTest={() => connectivityMutation.mutate()}
                onRefreshSettings={() => settingsQuery.refetch()}
                formatDateTime={formatDateTimeForLocale}
              />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </VStack>
      <DocumentationContentConfirmDialog
        dialog={confirmDialog}
        publishedVersionCodes={publishedVersionCodes}
        isLoading={
          publishMutation.isPending ||
          archiveMutation.isPending ||
          setDefaultMutation.isPending ||
          deleteMutation.isPending ||
          quickSyncMutation.isPending ||
          rebuildMutation.isPending ||
          forcePublishedSyncMutation.isPending
        }
        onClose={closeConfirmDialog}
        onConfirm={handleConfirmAction}
        onUpdateDialog={setConfirmDialog}
      />
    </Box>
  )
}
